import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, PlusCircle, HardHat, PieChart,
  History, Users, Settings, Menu, LogOut, Building2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { HOROZ_LOGO } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

function ProjectBadge() {
  const { project } = useProject();
  return (
    <div className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5" data-testid="project-badge">
      <HardHat className="h-4 w-4 text-slate-500" />
      <span className="hidden text-[10px] uppercase tracking-[0.18em] text-slate-400 sm:inline">Proje</span>
      <span className="font-heading text-sm font-bold tracking-tight">{project.name}</span>
    </div>
  );
}

const NAV = [
  { label: "Gösterge Paneli", icon: LayoutDashboard, path: "/", end: true },
  { label: "Giderler", icon: Receipt, path: "/expenses" },
  { label: "Yeni Gider", icon: PlusCircle, path: "/expenses/new" },
  { label: "İş Kalemleri", icon: HardHat, path: "/trades" },
  { label: "Raporlar", icon: PieChart, path: "/reports" },
  { label: "Geçmiş", icon: History, path: "/history" },
  { label: "Kullanıcılar", icon: Users, path: "/users", adminOnly: true },
  { label: "Ayarlar", icon: Settings, path: "/settings" },
];

function SidebarContent({ onNavigate }) {
  const { user, logout, isAdmin } = useAuth();
  const items = NAV.filter((i) => !i.adminOnly || isAdmin);
  return (
    <div className="flex h-full flex-col bg-primary text-primary-foreground">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 p-1">
          <img src={HOROZ_LOGO} alt="Horoz Group" className="h-full w-full object-contain" />
        </div>
        <div className="leading-tight">
          <div className="font-heading text-sm font-bold tracking-wide">HOROZ GROUP</div>
          <div className="text-[11px] text-white/50">Şantiye Gider Takip</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.path === "/" ? "dashboard" : item.path.replace(/\//g, "-").replace(/^-/, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-white/60 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-sm font-semibold">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-medium">{user?.name}</div>
            <div className="text-[11px] text-white/50">{user?.role === "admin" ? "Yönetici" : "Kullanıcı"}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={logout}
          data-testid="logout-button"
          className="mt-1 w-full justify-start gap-3 text-white/60 hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-[18px] w-[18px]" />
          Çıkış Yap
        </Button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block">
        <SidebarContent />
      </aside>

      <div className="flex w-full flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 border-0 p-0">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <ProjectBadge />
          <Button size="icon" onClick={() => navigate("/expenses/new")} data-testid="mobile-new-expense">
            <PlusCircle className="h-5 w-5" />
          </Button>
        </header>

        <div className="sticky top-0 z-20 hidden items-center justify-end border-b border-border bg-background/80 px-8 py-3 backdrop-blur lg:flex">
          <ProjectBadge />
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

export { NAV };
